import { z } from 'zod';

import { FUNDS_OWNERSHIPS, type FundsOwnership } from '@/constants/institution';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsV2BusinessProfile,
  type InstitutionDetailsV2BusinessProfileClients,
  institutionDetailsV2BusinessProfileBaseSchema,
  institutionDetailsV2BusinessProfileSchema,
} from '@/services/api/profile';

export const institutionBusinessProfileOverviewSchema = institutionDetailsV2BusinessProfileBaseSchema.pick({ totalAssets: true, annualRevenue: true, employees: true }).extend({
  // useFieldArray only manages arrays of objects
  topExchanges: z
    .array(z.object({ name: institutionDetailsV2BusinessProfileBaseSchema.shape.topExchanges.element }))
    .max(5, i18n.t('institutionDetails.businessProfile.errors.topExchangesTooMany')),
  fundsOwnership: z.enum(FUNDS_OWNERSHIPS, { error: i18n.t('institutionDetails.businessProfile.errors.fundsOwnership') }),
});

export type InstitutionBusinessProfile = z.infer<typeof institutionBusinessProfileOverviewSchema> & {
  fundsOwnership: FundsOwnership;
} & Partial<InstitutionDetailsV2BusinessProfileClients>;

export type InstitutionBusinessProfileOrDefaults = InstitutionBusinessProfile | Partial<InstitutionBusinessProfile>;

export const INSTITUTION_BUSINESS_PROFILE_DEFAULTS: InstitutionBusinessProfileOrDefaults = { topExchanges: [] };

// Parsing drops the client answers left over from an earlier choice when the company trades only its own funds
export const institutionBusinessProfileToInstitutionDetailsBusinessProfileApi = ({
  topExchanges,
  ...businessProfile
}: InstitutionBusinessProfile): Promise<InstitutionDetailsV2BusinessProfile> =>
  institutionDetailsV2BusinessProfileSchema.parseAsync({
    ...businessProfile,
    topExchanges: topExchanges.map(({ name }) => name),
  });

export const institutionDetailsBusinessProfileApiToInstitutionBusinessProfile = ({
  topExchanges,
  ...businessProfile
}: InstitutionDetailsV2BusinessProfile): InstitutionBusinessProfile => ({
  ...businessProfile,
  topExchanges: topExchanges.map((name) => ({ name })),
});
